<template>
  <section 
    class="cta-section" 
    :class="customClass"
    :style="backgroundImageStyle"
  >
    <div class="cta-overlay"></div>
    <div class="container">
      <div class="row align-items-center">
        <div :class="containerClass">
          <div class="cta-content" :class="contentClass">
            <slot name="title">
              <h2 class="cta-title" v-if="title">{{ title }}</h2>
            </slot>
            <slot name="subtitle-large">
              <h3 class="cta-subtitle-large" v-if="subtitleLarge">{{ subtitleLarge }}</h3>
            </slot>
            <slot name="description">
              <p class="cta-description" v-if="description">{{ description }}</p>
            </slot>
            <div class="cta-buttons" v-if="$slots.buttons || $slots.default || (buttons && buttons.length > 0)">
              <slot name="buttons">
                <slot>
                  <router-link 
                    v-for="(button, index) in buttons" 
                    :key="index"
                    :to="button.to || '#'" 
                    :class="button.class || 'btn btn-cta-primary btn-lg'"
                    @click="button.onClick"
                  >
                    {{ button.text }}
                    <i v-if="button.showArrow" class="fas fa-arrow-right ml-2"></i>
                  </router-link>
                </slot>
              </slot>
            </div>
          </div>
        </div>
        <div class="col-lg-6" v-if="showRightColumn">
          <!-- Espacio para la imagen (se ve en el fondo) -->
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "CTASection",
  props: {
    title: {
      type: String,
      default: ""
    },
    subtitleLarge: {
      type: String,
      default: ""
    },
    description: {
      type: String,
      default: ""
    },
    buttons: {
      type: Array,
      default: () => []
    },
    backgroundImage: {
      type: String,
      default: ""
    },
    containerClass: {
      type: String,
      default: "col-lg-6"
    },
    customClass: {
      type: String,
      default: ""
    },
    contentClass: {
      type: String,
      default: "animate-on-scroll fade-in-up"
    },
    showRightColumn: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    backgroundImageStyle() {
      if (this.backgroundImage) {
        return {
          backgroundImage: `url(${this.backgroundImage})`
        };
      }
      return {};
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.cta-section {
  position: relative;
  min-height: 500px;
  padding: 100px 0;
  color: white;
  display: flex;
  align-items: center;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  
  .cta-overlay {
    display: none;
    
    @media (max-width: 768px) {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, 
        $landing-primary-rgba-85 0%, 
        $landing-secondary-rgba-70 50%, 
        $landing-primary-rgba-60 100%);
      z-index: 1;
    }
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
  
  .cta-content {
    max-width: 600px;
    margin: 0;
    padding-right: 30px;
    position: relative;
    z-index: 3;
  }
  
  .cta-title {
    font-size: 4rem;
    font-weight: 700;
    margin-bottom: 1rem;
    line-height: 1.2;
    color: white;
    text-shadow: 0 4px 15px $landing-primary-rgba-40, 0 2px 8px rgba(0, 0, 0, 0.2);
    letter-spacing: -0.5px;
    white-space: nowrap;
    
    .cta-title-highlight {
      color: $landing-secondary-color;
      text-shadow: 
        0.5px 0.5px 0px rgba(0, 0, 0, 0.3),
        -0.5px -0.5px 0px rgba(0, 0, 0, 0.3),
        0.5px -0.5px 0px rgba(0, 0, 0, 0.3),
        -0.5px 0.5px 0px rgba(0, 0, 0, 0.3),
        0 4px 15px rgba(34, 207, 224, 0.6), 
        0 2px 8px rgba(34, 207, 224, 0.4);
      -webkit-text-stroke: 0.3px rgba(0, 0, 0, 0.2);
      paint-order: stroke fill;
    }
  }
  
  .cta-subtitle-large {
    font-size: 4rem;
    font-weight: 700;
    margin-bottom: 2rem;
    line-height: 1.3;
    color: white;
    text-shadow: 0 4px 15px $landing-primary-rgba-40, 0 2px 8px rgba(0, 0, 0, 0.2);
    letter-spacing: -0.3px;
  }
  
  .cta-description {
    font-size: 2rem;
    margin-bottom: 3rem;
    color: white;
    font-weight: 500;
    line-height: 1.7;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  .cta-buttons {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
  }
  
  .btn-cta-primary {
    background: $landing-primary-gradient;
    color: white;
    border: none;
    border-radius: 50px;
    padding: 20px 50px;
    font-size: 1.4rem;
    font-weight: 700;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 10px 30px $landing-primary-rgba-45;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    letter-spacing: 0.5px;
    
    &:hover {
      background: $landing-primary-gradient-dark;
      transform: translateY(-4px) scale(1.02);
      box-shadow: 0 15px 40px $landing-primary-rgba-55;
      color: white;
      text-decoration: none;
    }
    
    i {
      margin-left: 0.5rem;
    }
  }
  
  @media (max-width: 991px) {
    min-height: 450px;
    padding: 80px 0;
    
    .cta-title {
      font-size: 3rem;
    }
    
    .cta-subtitle-large {
      font-size: 3rem;
    }
    
    .cta-description {
      font-size: 1.5rem;
      margin-bottom: 2.5rem;
    }
    
    .btn-cta-primary {
      padding: 18px 45px;
    }
  }
  
  @media (max-width: 768px) {
    min-height: 400px;
    padding: 60px 0;
    
    .cta-content {
      padding-right: 0;
      max-width: 100%;
    }
    
    .cta-title {
      font-size: 2.5rem;
      margin-bottom: 1rem;
      white-space: normal;
    }
    
    .cta-subtitle-large {
      font-size: 2.5rem;
      margin-bottom: 1.5rem;
    }
    
    .cta-description {
      font-size: 1.3rem;
      margin-bottom: 2.5rem;
    }
    
    .btn-cta-primary {
      font-size: 1.15rem;
      padding: 16px 40px;
      padding: 15px 35px;
      font-size: 1.1rem;
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
